import React from "react";
import classNames from "classnames";
import { BemHelper } from "../../utils/BemHelper";
import { truncateHtmlToPlainText, useBreakpointValue } from "../../utils";
import { CalendarIconClean } from "@/icons/primary";
import defaultImage from "./images/logo-bkg-4.jpg";
import { CriyatorAvatarLabel } from "../criyator-avatar-label";

type Props = {
  type: "article" | "group-sessions" | "profile" | "product";
  title: string;
  description?: string;
  price?: string;
  createDate?: string;
  btn?: React.ReactNode;
  image?: string;
  userName?: string;
  userAvatar?: string;
  onClick?: () => void;
};

const bem = new BemHelper("expert-card");
const articleBem = new BemHelper("expert-article");
const groupsSessionBem = new BemHelper("expert-groups-session");
const profileBem = new BemHelper("expert-profile");
const productBem = new BemHelper("expert-product");

export const ExpertCard: React.FC<Props> = ({
  type,
  title,
  description,
  price,
  createDate,
  btn,
  image,
  userName,
  userAvatar,
  onClick,
}) => {
  const isMobile = useBreakpointValue({ base: false, xs: true });

  return (
    <div
      className={classNames(bem.block())}
      style={{ cursor: onClick ? "pointer" : "default" }}
      onClick={() => {
        onClick && onClick();
      }}
    >
      {/* Article */}
      {type === "article" && (
        <div className={classNames(articleBem.block(), isMobile && articleBem.modifier("mobile"))}>
          <div
            className={classNames(
              articleBem.element("image"),
              isMobile && articleBem.modifier("mobile", "image"),
            )}
            style={{
              border: image && image.length > 0 ? "none" : "1px solid #f5f3f3",
              background: `url('${image && image.length > 0 ? image : defaultImage}')`,
            }}
          />

          <div className={classNames(articleBem.element("wrapper"))}>
            {title && (
              <div
                className={classNames(
                  articleBem.element("title"),
                  isMobile && articleBem.modifier("mobile", "title"),
                )}
              >
                {title}
              </div>
            )}
            {createDate && (
              <div
                className={classNames(
                  articleBem.element("date"),
                  isMobile && articleBem.modifier("mobile", "date"),
                )}
              >
                <span style={{ marginRight: "10px" }}>
                  <CalendarIconClean style={{ fontSize: "24px" }} />
                </span>
                {createDate}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Group Sessions */}
      {type === "group-sessions" && (
        <div
          className={classNames(
            groupsSessionBem.block(),
            isMobile && groupsSessionBem.modifier("mobile"),
          )}
        >
          <div
            className={classNames(
              groupsSessionBem.element("image"),
              isMobile && groupsSessionBem.modifier("mobile", "image"),
            )}
            style={{
              border: image && image.length > 0 ? "none" : "1px solid #f5f3f3",
              background: `url('${image && image.length > 0 ? image : defaultImage}')`,
            }}
          />

          <div className={classNames(groupsSessionBem.element("wrapper"))}>
            {title && (
              <div
                className={classNames(
                  groupsSessionBem.element("title"),
                  isMobile && groupsSessionBem.modifier("mobile", "title"),
                )}
              >
                {title}
              </div>
            )}
            {createDate && (
              <div
                className={classNames(
                  groupsSessionBem.element("cohort"),
                  isMobile && groupsSessionBem.modifier("mobile", "cohort"),
                )}
              >
                Cohort: {createDate}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Profile */}
      {type === "profile" && (
        <div
          className={classNames(profileBem.block(), isMobile && profileBem.modifier("mobile"))}
          style={{
            border: image && image.length > 0 ? "none" : "1px solid #f5f3f3",
            background: `url('${image && image.length > 0 ? image : defaultImage}')`,
          }}
        >
          <div className={classNames(profileBem.element("bkg"))} />
          <div
            className={classNames(
              profileBem.element("wrapper"),
              isMobile && profileBem.modifier("mobile", "wrapper"),
            )}
          >
            {title && (
              <div
                className={classNames(
                  profileBem.element("title"),
                  isMobile && profileBem.modifier("mobile", "title"),
                )}
              >
                {title}
              </div>
            )}
            {description && (
              <div
                className={classNames(
                  profileBem.element("description"),
                  isMobile && profileBem.modifier("mobile", "description"),
                )}
              >
                {truncateHtmlToPlainText(description, 100)}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Product */}
      {type === "product" && (
        <div className={classNames(productBem.block(), isMobile && productBem.modifier("mobile"))}>
          {/* Main image */}
          <div
            className={classNames(
              productBem.element("image"),
              isMobile && productBem.modifier("mobile", "image"),
            )}
            style={{
              border: image && image.length > 0 ? "none" : "1px solid #f5f3f3",
              background: `url('${image && image.length > 0 ? image : defaultImage}')`,
            }}
          />
          <div
            className={classNames(
              productBem.element("content"),
              isMobile && productBem.modifier("mobile", "content"),
            )}
          >
            {(userAvatar || userName) && (
              <div
                className={classNames(
                  productBem.element("info-user"),
                  productBem.modifier("mobile", "info-user"),
                )}
              >
                <CriyatorAvatarLabel name={userName} pictureUrl={userAvatar} />
              </div>
            )}

            {/* Description block */}
            {title && (
              <div
                className={classNames(
                  productBem.element("title"),
                  isMobile && productBem.modifier("mobile", "title"),
                )}
              >
                {title}
              </div>
            )}

            {price !== undefined && (
              <div
                className={classNames(
                  productBem.element("price"),
                  isMobile && productBem.modifier("mobile", "price"),
                )}
              >
                {+price > 0 ? price + "$" : "FREE"}
              </div>
            )}
            {description && (
              <div
                className={classNames(
                  productBem.element("description"),
                  isMobile && productBem.modifier("mobile", "description"),
                )}
              >
                {truncateHtmlToPlainText(description, 100)}
              </div>
            )}
            <div style={{ marginTop: "auto" }}>
              {btn && <div className={classNames(productBem.element("btn"))}>{btn}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
